<template>
  <AjaxBar
    ref="bar"
    position="top"
    animate="false"
    color="primary"
    size="2px"
    :hijack-filter="ajax_bar_filter"
    indeterminate
  />
  <q-layout view="lHh LpR lFf">
    <q-header
      bordered
      class="bg-page"
    >
      <q-toolbar
        class="row items-center"
        style="height: 64px"
      >
        <!-- Left Side: Drawer Button and Breadcrumb -->
        <div class="col-xs-2 col-sm-3 col-md-4 col-lg-4 col-xl-4 row items-center">
          <q-btn
            dense
            flat
            round
            :icon="drawer ? 'menu_open' : 'menu'"
            color="primary"
            @click="toggleDrawer"
          />
          <!-- Breadcrumb, hidden on smaller screens -->
          <div class="gt-sm items-center">
            <template v-if="!drawer">
              <span class="text-default q-pl-md">{{
                $page.props.shared.workspace.name
              }}</span>
            </template>
          </div>
        </div>

        <!-- Center: Omnisearch and Currency Converter -->
        <div class="text-default col-xs-6 col-sm-6 col-md-4 col-lg-4 col-xl-4 row items-center">
          <q-select
            ref="omnisearch"
            outlined
            rounded
            dense
            options-dense
            :loading="omnisearch_loading"
            :model-value="omnisearch_results_query"
            :options="omnisearch_history_show"
            use-input
            fill-input
            hide-selected
            input-debounce="300"
            :placeholder="shared.omnisearch.placeholder"
            class="omnisearch-input"
            style="flex: 1; margin-right: 8px"
            hide-dropdown-icon
            clearable
            @filter="omnisearch_update"
            @clear="$refs.omnisearch.updateInputValue('')"
          >
            <template v-slot:prepend>
              <q-icon name="search" />
            </template>

            <template
              v-if="omnisearch_results_query != ''"
              v-slot:no-option
            >
              <!-- v-slot:no-option is used for displaying search results, v-slot:option is used for displaying search history -->
              <div class="search-results-card q-pa-md fit">
                <!-- Omnisearch No Results -->
                <div
                  v-if="omnisearch_results_empty"
                  class="text-center"
                >
                  <q-icon
                    name="search_off"
                    size="lg"
                  />
                  <p class="text-muted">
                    {{ shared.omnisearch.results_empty }}
                  </p>
                </div>

                <!-- Omnisearch Results -->
                <!-- Omnisearch Exchange account -->
                <template v-if="omnisearch_results.exchange_accounts.length > 0">
                  <div class="text-caption q-mb-sm">
                    {{ shared.omnisearch.results_header_exchange_accounts }}
                  </div>
                  <div
                    v-for="exchange_account in omnisearch_results.exchange_accounts"
                    :key="exchange_account.id"
                  >
                    <q-item
                      v-close-popup
                      clickable
                      @click="omnisearch_results_select(exchange_account)"
                    >
                      <q-item-section avatar>
                        <q-avatar
                          size="32px"
                          square
                        >
                          <img
                            :src="exchange_account.logo_url"
                            :alt="exchange_account.exchange"
                          >
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label class="text-bold">
                          {{ exchange_account.name }}
                        </q-item-label>
                      </q-item-section>

                      <q-item-section
                        side
                        style="display: inline"
                      >
                        <!-- Api key -->
                        <q-btn
                          v-if="exchange_account.api_key"
                          unelevated
                          flat
                          round
                          color="primary"
                          size="sm"
                          icon="las la-copy"
                          @click.stop="
                            clipboardCopy(exchange_account.api_key);
                            emits('addToHistory');
                          "
                        >
                          <q-tooltip
                            :delay="500"
                            anchor="bottom middle"
                            self="top middle"
                          >
                            {{
                              shared.omnisearch
                                .results_exchange_accounts_api_key_copy
                            }}
                          </q-tooltip>
                        </q-btn>

                        <!-- Go to transaction -->
                        <q-btn
                          unelevated
                          flat
                          round
                          color="primary"
                          size="sm"
                          icon="las la-search"
                          @click.stop="
                            omnisearch_results_select({
                              link: exchange_account.link_transactions,
                            })
                          "
                        >
                          <q-tooltip
                            :delay="500"
                            anchor="bottom middle"
                            self="top middle"
                          >
                            {{
                              shared.omnisearch
                                .results_exchange_accounts_transactions
                            }}
                          </q-tooltip>
                        </q-btn>

                        <!-- Public address -->
                        <q-btn
                          v-if="exchange_account.public_address"
                          unelevated
                          flat
                          round
                          color="primary"
                          size="sm"
                          icon="las la-copy"
                          @click.stop="
                            clipboardCopy(exchange_account.public_address);
                            emits('addToHistory');
                          "
                        >
                          <q-tooltip
                            :delay="500"
                            anchor="bottom middle"
                            self="top middle"
                          >
                            {{
                              shared.omnisearch
                                .results_exchange_accounts_public_address_copy
                            }}
                          </q-tooltip>
                        </q-btn>

                        <!-- Explore url -->
                        <q-btn
                          v-if="exchange_account.link_explorer"
                          unelevated
                          flat
                          round
                          color="primary"
                          size="sm"
                          target="_blank"
                          icon="las la-external-link-alt"
                          :href="exchange_account.link_explorer"
                        >
                          <q-tooltip
                            :delay="500"
                            anchor="bottom middle"
                            self="top middle"
                          >
                            {{
                              shared.omnisearch
                                .results_exchange_accounts_explorer
                            }}
                          </q-tooltip>
                        </q-btn>
                      </q-item-section>
                    </q-item>
                    <!--
                    <OmnisearchExchangeAccountItem
                      v-if="searchResult.type == 'exchange_account'"
                      :exchangeAccount="searchResult.item"
                      @addToHistory="addToHistory"
                    />
                    -->
                  </div>
                </template>

                <!-- Omnisearch Asset -->
                <template v-if="omnisearch_results.assets.length > 0">
                  <div class="text-caption q-mb-sm">
                    {{ shared.omnisearch.results_header_assets }}
                  </div>
                  <div
                    v-for="asset in omnisearch_results.assets"
                    :key="asset.id"
                  >
                    <q-item
                      v-close-popup
                      clickable
                      @click="omnisearch_results_select(asset)"
                    >
                      <q-item-section avatar>
                        <!-- Asset Icon -->
                        <img
                          width="28px"
                          height="28px"
                          class="inline q-mr-xs"
                          :alt="asset.symbol"
                          :src="asset.logo_url"
                        >
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>
                          <span class="text-bold q-pr-md ellipsis max-w[32ch]">{{ asset.symbol }}
                          </span>
                        </q-item-label>

                        <q-item-label class="text-muted ellipsis max-w[16ch]">
                          {{ asset.name }}
                        </q-item-label>
                      </q-item-section>

                      <q-item-section
                        side
                        style="display: inline"
                      >
                        <Link :href="asset.link">
                          <q-btn
                            unelevated
                            flat
                            round
                            color="primary"
                            size="sm"
                            icon="las la-search"
                          >
                            <q-tooltip
                              :delay="500"
                              anchor="bottom middle"
                              self="top middle"
                            >
                              {{ asset.link_label }}
                            </q-tooltip>
                          </q-btn>

                          <q-btn
                            v-if="asset.link_external"
                            flat
                            round
                            color="primary"
                            icon="las la-external-link-alt"
                            target="_blank"
                            size="sm"
                            :href="asset.link_external"
                            @click.stop
                          />
                        </Link>
                      </q-item-section>
                    </q-item>
                  </div>
                </template>

                <!-- Omnisearch Transaction Kinds -->
                <template v-if="omnisearch_results.transaction_kinds.length > 0">
                  <div class="text-caption q-mb-sm">
                    {{ shared.omnisearch.results_header_transaction_kinds }}
                  </div>
                  <div
                    v-for="transaction_kind in omnisearch_results.transaction_kinds"
                    :key="transaction_kind.id"
                  >
                    <q-item
                      v-close-popup
                      clickable
                      @click="omnisearch_results_select(transaction_kind)"
                    >
                      <q-item-section avatar>
                        <q-avatar
                          :color="transaction_kind.icon.color"
                          size="32px"
                          class="q-mr-sm"
                        >
                          <q-icon
                            :name="transaction_kind.icon.name"
                            color="black"
                            size="20px"
                            class=""
                          />
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>
                          <span class="text-bold q-pr-md ellipsis max-w[32ch]">{{ transaction_kind.label }}</span>
                        </q-item-label>
                        <q-item-label class="text-muted ellipsis max-w[32ch]">
                          {{ transaction_kind.name }}
                        </q-item-label>
                      </q-item-section>

                      <q-item-section
                        side
                        style="display: inline"
                      >
                        <q-btn
                          unelevated
                          flat
                          round
                          color="primary"
                          size="sm"
                          icon="las la-search"
                        >
                          <q-tooltip
                            :delay="500"
                            anchor="bottom middle"
                            self="top middle"
                          >
                            {{ transaction_kind.link_label }}
                          </q-tooltip>
                        </q-btn>
                      </q-item-section>
                    </q-item>
                  </div>
                </template>

                <!-- Omnisearch Page -->
                <template v-if="omnisearch_results.pages.length > 0">
                  <div class="text-caption q-mb-sm">
                    {{ shared.omnisearch.results_header_pages }}
                  </div>
                  <div
                    v-for="(page, index) in omnisearch_results.pages"
                    :key="index"
                  >
                    <q-item
                      v-close-popup
                      clickable
                      @click="omnisearch_results_select(page)"
                    >
                      <q-item-section avatar>
                        <q-avatar
                          color="info"
                          size="28px"
                        >
                          <q-icon
                            name="article"
                            color="white"
                          />
                        </q-avatar>
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>
                          <span class="text-bold q-pr-md">{{ page.label }}
                          </span>
                        </q-item-label>

                        <q-item-label class="text-muted">
                          {{ page.description }}
                        </q-item-label>
                      </q-item-section>
                      <q-item-section side>
                        <q-icon
                          name="chevron_right"
                          size="22px"
                          class="text-muted"
                        />
                      </q-item-section>
                    </q-item>
                  </div>
                </template>
              </div>
            </template>

            <!-- Omnisearch History -->
            <template v-slot:option="scope">
              <q-item
                v-close-popup
                clickable
                @click="omnisearch_history_select(scope.opt)"
              >
                <q-item-section avatar>
                  <q-icon
                    size="22px"
                    name="las la-history"
                    class="text-muted"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ scope.opt }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    size="sm"
                    icon="close"
                    flat
                    dense
                    round
                    @click.stop="omnisearch_history_delete(scope.opt)"
                  />
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <!-- Currency Converter -->
          <q-btn
            round
            flat
            icon="currency_exchange"
            color="primary"
            size="md"
          >
            <q-tooltip
              :delay="500"
              class="bg-primary"
            >
              {{ shared.currency_converter.title }}
            </q-tooltip>
            <q-menu
              anchor="bottom right"
              self="top right"
              :offset="[0, 8]"
              class="currency-converter__menu"
            >
              <q-card class="transactions-settings-dialog__form q-pa-lg">
                <q-card-section class="row items-center q-pb-none">
                  <div class="col text-h6">
                    {{ shared.currency_converter.title }}
                  </div>
                  <q-btn
                    v-close-popup
                    icon="close"
                    flat
                    round
                    dense
                  />
                </q-card-section>

                <q-card-section class="q-pt-md">
                  <q-form class="currency-converter__form">
                    <div class="q-mb-lg">
                      <div class="row q-col-gutter-sm">
                        <div class="col-12 col-sm-6">
                          <span class="text-input-label">{{ shared.currency_converter.amount_label }}</span>
                          <q-input
                            v-model="currency_converter_amount"
                            type="number"
                            outlined
                            dense
                            hide-bottom-space
                            step="0.01"
                            min="0"
                            class="full-width q-mt-sm"
                          />
                        </div>
                        <div class="col-12 col-sm-6">
                          <span class="text-input-label">{{ shared.currency_converter.asset_label }}</span>
                          <q-select
                            :model-value="currency_converter_results.asset"
                            :options="currency_converter_results.assets"
                            option-label="full_name"
                            outlined
                            dense
                            hide-bottom-space
                            use-input
                            hide-selected
                            fill-input
                            input-debounce="300"
                            class="full-width q-mt-sm"
                            :loading="currency_converter_search_loading"
                            @update:model-value="currency_converter_asset_update"
                            @filter="currency_converter_search_update"
                          >
                            <template v-slot:option="scope">
                              <q-item v-bind="scope.itemProps">
                                <q-item-section avatar>
                                  <q-avatar
                                    size="24px"
                                    square
                                  >
                                    <img
                                      :src="scope.opt.logo_url"
                                      :alt="scope.opt.symbol"
                                    >
                                  </q-avatar>
                                </q-item-section>
                                <q-item-section>
                                  <q-item-label>{{ scope.opt.name }}</q-item-label>
                                  <q-item-label caption>
                                    {{ scope.opt.symbol }}
                                  </q-item-label>
                                </q-item-section>
                              </q-item>
                            </template>

                            <template v-slot:no-option>
                              <q-item class="text-grey">
                                <q-item-section v-if="currency_converter_results.search.length > 0">
                                  {{ shared.currency_converter.no_results }}
                                </q-item-section>
                                <q-item-section v-else>
                                  {{ shared.currency_converter.search_placeholder }}
                                </q-item-section>
                              </q-item>
                            </template>

                            <template v-slot:append>
                              <q-btn
                                v-if="currency_converter_results.asset?.link_external"
                                flat
                                round
                                dense
                                icon="open_in_new"
                                color="primary"
                                size="sm"
                                :href="currency_converter_results.asset?.link_external"
                                target="_blank"
                                @click.stop
                                @mousedown.stop.prevent
                              >
                                <q-tooltip
                                  anchor="top middle"
                                  :offset="[0, 35]"
                                >
                                  {{ currency_converter_results.asset?.link_external_title }}
                                </q-tooltip>
                              </q-btn>
                            </template>
                          </q-select>
                        </div>
                      </div>
                    </div>

                    <div class="q-mb-lg">
                      <div class="row items-center justify-between">
                        <span class="text-input-label">{{ shared.currency_converter.range_toggle_label }}</span>
                        <q-toggle
                          v-model="currency_converter_range_enabled"
                          color="accent"
                          @update:model-value="currency_converter_toggle_range"
                        />
                      </div>

                      <div
                        v-if="!currency_converter_range_enabled"
                        class="q-mt-sm"
                      >
                        <q-input
                          :model-value="formatDateForDisplay(
                            currency_converter_selected_date
                          ) || currency_converter_results.date_formatted
                          "
                          :label="shared.currency_converter.date_label"
                          outlined
                          dense
                          hide-bottom-space
                          readonly
                        >
                          <q-menu
                            anchor="bottom left"
                            self="top left"
                            transition-show="scale"
                            transition-hide="scale"
                          >
                            <q-date
                              :model-value="currency_converter_selected_date ||
                                currency_converter_results.date
                              "
                              minimal
                              :navigation-max-year-month="currency_converter_results.date_max
                              "
                              mask="YYYY-MM-DD"
                              @update:model-value="currency_converter_date_update"
                            />
                          </q-menu>
                        </q-input>
                      </div>
                      <div
                        v-else
                        class="q-mt-sm"
                      >
                        <div class="row q-col-gutter-sm">
                          <q-input
                            :model-value="formatDateForDisplay(currency_converter_date_from) ||
                              currency_converter_results.date_from_formatted
                            "
                            :label="shared.currency_converter.from_label"
                            outlined
                            dense
                            hide-bottom-space
                            class="col-12 col-sm-6"
                          >
                            <q-menu
                              anchor="bottom left"
                              self="top left"
                              transition-show="scale"
                              transition-hide="scale"
                            >
                              <q-date
                                :model-value="currency_converter_date_from"
                                minimal
                                :navigation-max-year-month="currency_converter_results.date_max
                                "
                                mask="YYYY-MM-DD"
                                @update:model-value="
                                  currency_converter_date_from_update
                                "
                              />
                            </q-menu>
                          </q-input>
                          <q-input
                            :model-value="formatDateForDisplay(currency_converter_date_to) ||
                              currency_converter_results.date_to_formatted
                            "
                            :label="shared.currency_converter.to_label"
                            outlined
                            dense
                            hide-bottom-space
                            class="col-12 col-sm-6"
                          >
                            <q-menu
                              anchor="bottom left"
                              self="top left"
                              transition-show="scale"
                              transition-hide="scale"
                            >
                              <q-date
                                :model-value="currency_converter_date_to"
                                minimal
                                :navigation-max-year-month="currency_converter_results.date_max
                                "
                                mask="YYYY-MM-DD"
                                @update:model-value="
                                  currency_converter_date_to_update
                                "
                              />
                            </q-menu>
                          </q-input>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="!currency_converter_range_enabled"
                      class="q-mb-lg"
                    >
                      <span class="text-input-label">{{ shared.currency_converter.result_label }}</span>
                      <q-input
                        :model-value="currency_converter_result"
                        outlined
                        dense
                        readonly
                        hide-bottom-space
                        class="full-width q-mt-sm cursor-pointer"
                      >
                        <template v-slot:prepend>
                          {{ currency_converter_results.fiat_symbol }}
                        </template>
                        <template v-slot:append>
                          <ClipboardCopy
                            v-if="currency_converter_result"
                            :content="currency_converter_result"
                          >
                            <q-icon
                              name="content_copy"
                              class="cursor-pointer"
                            />
                          </ClipboardCopy>
                        </template>
                      </q-input>
                    </div>

                    <div
                      v-if="currency_converter_range_enabled"
                      class="q-mt-lg"
                    >
                      <div class="row items-center justify-between q-mb-sm">
                        <div class="text-subtitle2 text-bold">
                          {{ shared.currency_converter.history_title }}
                        </div>
                        <q-btn
                          v-if="currency_converter_results.export_url"
                          flat
                          size="sm"
                          color="primary"
                          icon="file_download"
                          :label="shared.currency_converter.export_label"
                          @click="currency_converter_export_rates"
                        />
                      </div>
                      <div
                        v-if="currency_converter_rate_rows.length > 0"
                        class="currency-converter__table"
                      >
                        <q-table
                          flat
                          dense
                          :rows="currency_converter_rate_rows"
                          :columns="currency_converter_rate_columns"
                          row-key="date_iso"
                          hide-bottom
                          :pagination="{ rowsPerPage: 0 }"
                        />
                      </div>
                      <div
                        v-if="currency_converter_chart_data"
                        class="currency-converter__chart-wrapper"
                      >
                        <Line
                          :data="currency_converter_chart_data"
                          :options="currency_converter_chart_options"
                          :height="120"
                        />
                      </div>
                      <div
                        v-else
                        class="text-caption text-grey-7"
                      >
                        {{ shared.currency_converter.no_rates_in_range }}
                      </div>
                    </div>
                  </q-form>
                </q-card-section>
              </q-card>
            </q-menu>
          </q-btn>
        </div>

        <!-- Balance -->
        <div class="col-xs-3 col-sm-3 col-md-4 col-lg-4 col-xl-4 row justify-end items-center text-right col-grow">
          <div
            v-if="wacHeader"
            class="row items-center q-mr-md"
          >
            <!-- Can launch audit -->
            <q-chip
              v-if="wacHeader.update_status === 'outdated'"
              rounded
              clickable
              color="primary"
              outline
              class="btn-chip"
              @click="handleWacUpdate"
            >
              <q-icon
                size="18px"
                name="auto_awesome"
                class="q-mr-xs"
              />
              <span v-if="!isAuditChipCompact">
                {{ wacI18n.wac_update_action_update }}
              </span>
              <q-tooltip
                anchor="center left"
                self="center right"
                :delay="500"
                class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
              >
                <span v-html="wacI18n.wac_update_action_update_description" />
              </q-tooltip>
            </q-chip>

            <!-- Audit is running -->
            <q-chip
              v-else-if="wacHeader.update_status === 'calculating'"
              rounded
              class="shimmer-chip"
            >
              <q-spinner
                class="q-mr-xs shimmer-spinner"
                size="18px"
              />
              <span
                v-if="!isAuditChipCompact"
                class="loading-shimmer"
              >
                {{ wacI18n.wac_update_action_updating }} {{ wacHeader.progress || 0 }}%
              </span>
              <q-tooltip
                anchor="center left"
                self="top left"
                :delay="500"
                class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu row items-center"
              >
                {{ wacI18n.wac_update_action_updating }} {{ wacHeader.progress || 0 }}%
              </q-tooltip>
            </q-chip>

            <!-- Audit completed successfully (no errors) -->
            <q-chip
              v-else-if="wacHeader.update_status === 'uptodate' && !wacHeader.has_errors"
              rounded
              clickable
              outline
              class="btn-chip"
              @click="$inertia.visit(wacHeader.audit_page_url)"
            >
              <q-icon
                size="18px"
                name="check_circle"
                class="q-mr-xs"
                color="positive"
              />
              <span v-if="!isAuditChipCompact">
                {{ wacI18n.wac_update_completed }}
              </span>
              <q-tooltip
                anchor="center left"
                self="center right"
                :delay="500"
                class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
              >
                {{ wacI18n.wac_update_view_details }}
              </q-tooltip>
            </q-chip>

            <!-- Audit completed with errors -->
            <q-chip
              v-else-if="wacHeader.update_status === 'uptodate' && wacHeader.has_errors"
              rounded
              clickable
              outline
              class="btn-chip"
              @click="$inertia.visit(wacHeader.audit_page_url)"
            >
              <q-icon
                size="18px"
                name="error"
                class="q-mr-xs"
                color="negative"
              />
              <span v-if="!isAuditChipCompact">
                {{ wacI18n.wac_update_completed }}
              </span>
              <q-tooltip
                anchor="center left"
                self="center right"
                :delay="500"
                class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
              >
                {{ wacI18n.wac_update_view_details }}
              </q-tooltip>
            </q-chip>
          </div>
          <div class="gt-sm">
            <template v-if="!$page.props.shared.workspace.balance">
              <div class="total-balance q-mr-md">
                <q-spinner-dots
                  color="primary"
                  size="12px"
                />
              </div>
            </template>
            <template v-else>
              <span class="text-bold total-balance q-mb-none text-primary q-mr-md">
                {{ $page.props.shared.workspace.balance }}

                <q-tooltip
                  anchor="center left"
                  self="center right"
                  :delay="500"
                  class="q-card shadow text-caption text-default q-pa-md q-menu"
                >
                  {{ $page.props.shared.workspace.balance_last_update }}
                </q-tooltip>
              </span>
            </template>
          </div>

          <!-- Individual Regional Zone -->
          <Link
            class="q-mr-md"
            :href="$page.props.shared.zone.link"
          >
            <q-btn
              v-if="!$page.props.shared.workspace.is_business"
              outline
              style="border-radius: 8px"
              color="primary"
              padding="xs 16px"
            >
              <img
                src="/image/flag_fr.png"
                width="20"
                height="15"
                alt="French flag"
                style="margin-right: 12px"
              >
              {{ $page.props.shared.zone.label }}
              <q-tooltip
                :delay="500"
                class="q-card shadow text-caption text-default q-pa-md"
                style="width: 420px"
              >
                <span v-html="$page.props.shared.zone.note" />
              </q-tooltip>
            </q-btn>
          </Link>
          <UserMenu :locale="$page.props.auth.locale" />
        </div>
      </q-toolbar>
      <q-banner
        v-if="$page.props.shared.banner"
        class="workspace_layout_banner q-py-xs text-white"
        style="border-radius: 0px"
      >
        {{ $page.props.shared.banner }}
      </q-banner>
    </q-header>

    <q-drawer
      v-model="drawer"
      show-if-above
      bordered
      side="left"
      :width="290"
      class="bg-drawer"
      :class="{ 'workspace-drawer--mini': isMiniNavigation, 'q-px-md': !isMiniNavigation }"
      content-class=""
      :mini="isMiniNavigation"
      :mini-width="76"
    >
      <div class="column justify-between no-wrap fit">
        <!-- Workspace Selector -->
        <q-list class="">
          <div
            class="h-[64px] full-width row items-center justify-center"
            :class="isMiniNavigation ? 'q-px-none' : ''"
          >
            <q-btn-dropdown
              v-model="workspaces_selector_dropdown_show"
              color="white"
              text-color="dark"
              class="workspace-selector-btn"
              :class="{
                'workspace-selector-btn--mini': isMiniNavigation,
                'full-width': !isMiniNavigation,
              }"
              :round="isMiniNavigation"
              :dropdown-icon="isMiniNavigation ? 'none' : null"
              content-class="workspaces_selector_dropdown_menu"
              unelevated
              dense
            >
              <template v-slot:label>
                <q-avatar
                  :rounded="!isMiniNavigation"
                  color="transparent"
                  :class="{ 'q-pr-xs': !isMiniNavigation }"
                  :size="isMiniNavigation ? '36px' : '24px'"
                >
                  <Jdenticon
                    :text="shared.workspace.id"
                    :size="isMiniNavigation ? 28 : 18"
                  />
                </q-avatar>
                <div
                  v-if="!isMiniNavigation"
                  class="text-primary text-bold ellipsis w-[159px]"
                >
                  {{ shared.workspace.name }}
                </div>
              </template>
              <template v-slot:default>
                <div class="workspaces_selector_panel column fit no-wrap">
                  <div class="workspaces_selector_header q-pt-none q-pb-xs q-px-none">
                    <q-input
                      :model-value="shared.workspaces_selector.search_query"
                      autofocus
                      dense
                      outlined
                      class="q-my-xs"
                      debounce="300"
                      :placeholder="$q.lang.label.search"
                      @update:model-value="
                        workspaces_selector_search_update
                      "
                    >
                      <template v-slot:prepend>
                        <q-icon name="search" />
                      </template>
                      <template
                        v-if="shared.workspaces_selector.search_query.length > 0
                        "
                        v-slot:append
                      >
                        <q-icon
                          name="close"
                          class="cursor-pointer"
                          @click="workspaces_selector_search_update('')"
                        />
                      </template>
                    </q-input>

                    <!-- 'My Clients Only' checkbox - only displayed for workspace group admins -->
                    <div
                      v-if="
                        shared.workspaces_selector.filter_clients_enabled
                      "
                      class="q-my-md q-px-md"
                    >
                      <q-checkbox
                        :model-value="shared.workspaces_selector.filter_clients_value
                        "
                        :label="shared.workspaces_selector.filter_clients_label
                        "
                        dense
                        class="q-px-md"
                        @update:model-value="
                          workspaces_selector_filter_clients_update
                        "
                      />
                    </div>
                  </div>

                  <!-- Workspaces Selector - Results -->
                  <q-virtual-scroll
                    class="workspaces_selector_list"
                    :items="shared.workspaces_selector.workspaces"
                    virtual-scroll-item-size="32"
                  >
                    <template v-slot="{ item, index }">
                      <div class="q-px-md q-pb-sm">
                        <q-item
                          :key="index"
                          clickable
                          active-class="text-bold bg-transparent"
                          @click="workspaces_selector_click(item)"
                        >
                          <q-item-section avatar>
                            <q-avatar
                              rounded
                              color="transparent"
                              size="24px"
                            >
                              <Jdenticon
                                :text="item.id"
                                :size="18"
                              />
                            </q-avatar>
                          </q-item-section>
                          <q-item-section class="ellipsis">
                            {{ item.name }}
                          </q-item-section>
                          <q-item-section side>
                            <q-chip
                              v-if="item.professional"
                              size="10px"
                              class="workspace-chip"
                              label="PRO"
                            />
                          </q-item-section>
                        </q-item>
                      </div>
                    </template>
                  </q-virtual-scroll>

                  <!-- Workspaces Selector - Workspaces Button -->
                  <div class="workspaces_selector_footer">
                    <!-- No results message when no workspaces match the filter -->
                    <div
                      v-if="
                        shared.workspaces_selector.workspaces.length === 0
                      "
                      class="text-center q-pa-md"
                    >
                      <q-icon
                        name="search_off"
                        size="md"
                        color="grey-7"
                      />
                      <p class="text-caption q-mt-sm q-mb-none text-grey-7">
                        {{ shared.workspaces_selector.label_no_result }}
                      </p>
                    </div>

                    <q-separator />
                    <template v-if="shared.workspaces_selector.admin_action">
                      <div class="full-width text-center justify-between">
                        <Link :href="shared.workspaces_selector.see_all.link">
                          <q-btn
                            unelevated
                            color="primary"
                            flat
                            class="btn-underline q-py-sm text-bold"
                            :label="shared.workspaces_selector.see_all.label
                            "
                            no-caps
                          />
                        </Link>

                        <Link
                          :href="shared.workspaces_selector.admin_action.link
                          "
                        >
                          <q-btn
                            unelevated
                            color="primary"
                            flat
                            class="btn-underline q-py-sm text-bold"
                            :label="shared.workspaces_selector.admin_action.label
                            "
                            no-caps
                          />
                        </Link>
                      </div>
                    </template>
                    <template v-else>
                      <Link :href="shared.workspaces_selector.see_all.link">
                        <q-btn
                          unelevated
                          color="primary"
                          flat
                          class="full-width btn-underline q-py-sm text-bold"
                          :label="shared.workspaces_selector.see_all.label"
                          no-caps
                        />
                      </Link>
                    </template>
                  </div>
                </div>
                <span v-if="!workspaces_selector_dropdown_show">&nbsp;</span><!-- Removing this space prevents Firefox v137 from opening the dropdown -->
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.workspace.name }}
                </q-tooltip>
              </template>
            </q-btn-dropdown>
          </div>

          <!-- Main Menu -->
          <q-list class="q-pt-lg q-mt-md q-pb-sm">
            <!-- Dashboard -->
            <Link :href="shared.menu_items.dashboard.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.dashboard.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="shared.menu_items.dashboard.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.dashboard.title }}
                  </q-item-label>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.dashboard.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <!-- Imports -->
            <Link :href="shared.menu_items.imports.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.imports.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-spinner-ios
                    v-if="shared.menu_items.imports.loading"
                    size="18px"
                  />
                  <q-icon
                    v-else
                    :name="shared.menu_items.imports.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.imports.title }}
                  </q-item-label>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.imports.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <!-- Assets -->
            <Link :href="shared.menu_items.assets.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.assets.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="shared.menu_items.assets.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.assets.title }}
                  </q-item-label>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.assets.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <!-- Transactions -->
            <Link :href="shared.menu_items.transactions.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.transactions.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="shared.menu_items.transactions.icon"
                    size="18px"
                  />
                  <q-badge
                    v-if="isMiniNavigation && transactions_counts.uncategorized != 0"
                    class="menu-chip menu-chip--floating"
                    floating
                    rounded
                    style="font-size: 10px"
                  >
                    {{ transactions_counts.uncategorized }}
                  </q-badge>
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.transactions.title }}
                  </q-item-label>
                </q-item-section>

                <q-item-section
                  v-if="!isMiniNavigation"
                  side
                >
                  <q-badge
                    v-if="transactions_counts.uncategorized != 0"
                    class="q-mr-sm text-bold menu-chip"
                    rounded
                    style="font-size: 10px"
                  >
                    {{ transactions_counts.uncategorized }}
                  </q-badge>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.transactions.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <!-- Audit -->
            <Link
              v-if="shared.menu_items.audit"
              :href="shared.menu_items.audit.link"
            >
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.audit.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="shared.menu_items.audit.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.audit.title }}
                  </q-item-label>
                </q-item-section>
                <q-item-section
                  v-if="!isMiniNavigation && auditIssuesCount > 0"
                  side
                >
                  <q-badge
                    class="q-mr-sm text-bold menu-chip"
                    rounded
                    color="negative"
                    text-color="white"
                    style="font-size: 10px"
                  >
                    {{ auditIssuesCount }}
                  </q-badge>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.audit.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <!-- Reports -->
            <Link :href="shared.menu_items.reports.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.reports.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-spinner-ios
                    v-if="shared.menu_items.reports.loading"
                    size="18px"
                  />
                  <q-icon
                    v-else
                    :name="shared.menu_items.reports.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.reports.title }}
                  </q-item-label>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.reports.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <!-- Tax information -->
            <Link :href="shared.menu_items.tax_information.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.tax_information.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="shared.menu_items.tax_information.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.tax_information.title }}
                  </q-item-label>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.tax_information.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <q-separator />

            <!-- Settings -->
            <Link :href="shared.menu_items.settings.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.settings.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="shared.menu_items.settings.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.settings.title }}
                  </q-item-label>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.settings.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <!-- Subscription -->
            <Link :href="shared.menu_items.subscription.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.subscription.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="shared.menu_items.subscription.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.subscription.title }}
                  </q-item-label>
                </q-item-section>

                <q-item-section
                  v-if="shared.menu_items.subscription.warning && !isMiniNavigation"
                  side
                >
                  <q-icon
                    name="error"
                    color="negative"
                  />
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.subscription.title }}
                </q-tooltip>
              </q-item>
            </Link>

            <!-- Centre d'aide -->
            <q-item
              clickable
              dense
              style="min-height: 38px"
              class="q-my-sm main-menu-link q-mt-none"
              :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              tag="a"
              :href="shared.menu_items.help_center.link"
              target="_blank"
              rel="noopener noreferrer"
            >
              <q-item-section avatar>
                <q-icon
                  :name="shared.menu_items.help_center.icon"
                  size="18px"
                />
              </q-item-section>

              <q-item-section v-if="!isMiniNavigation">
                <q-item-label
                  class=""
                  style="font-weight: 500; font-size: 14px"
                >
                  {{ shared.menu_items.help_center.title }}
                </q-item-label>
              </q-item-section>
              <q-tooltip v-if="isMiniNavigation">
                {{ shared.menu_items.help_center.title }}
              </q-tooltip>
            </q-item>

            <!-- Partners -->
            <Link :href="shared.menu_items.partners.link">
              <q-item
                clickable
                dense
                style="min-height: 38px"
                :active="shared.menu_items.partners.is_active"
                class="q-my-sm main-menu-link q-mt-none"
                :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              >
                <q-item-section avatar>
                  <q-icon
                    :name="shared.menu_items.partners.icon"
                    size="18px"
                  />
                </q-item-section>

                <q-item-section v-if="!isMiniNavigation">
                  <q-item-label
                    class=""
                    style="font-weight: 500; font-size: 14px"
                  >
                    {{ shared.menu_items.partners.title }}
                  </q-item-label>
                </q-item-section>
                <q-tooltip v-if="isMiniNavigation">
                  {{ shared.menu_items.partners.title }}
                </q-tooltip>
              </q-item>
            </Link>
          </q-list>

          <!-- Subscribe CTA -->
          <template v-if="shared.subscription_cta">
            <q-item
              clickable
              dense
              style="min-height: 38px"
              class="q-my-sm main-menu-link q-mt-none bg-pastel-yellow text-black"
              :class="{ 'main-menu-link--mini': isMiniNavigation, 'q-px-xs': isMiniNavigation }"
              @click="$inertia.get($page.props.shared.subscription_cta.link)"
            >
              <q-item-section avatar>
                <q-icon
                  name="sym_o_workspace_premium"
                  size="18px"
                />
              </q-item-section>

              <q-item-section v-if="!isMiniNavigation">
                <q-item-label
                  class=""
                  style="font-weight: 500; font-size: 14px"
                >
                  {{ $page.props.shared.subscription_cta.title }}
                </q-item-label>
              </q-item-section>
              <q-tooltip v-if="isMiniNavigation">
                {{ $page.props.shared.subscription_cta.title }}
              </q-tooltip>
            </q-item>
          </template>
        </q-list>

        <!-- Spacer -->
        <q-item class="col-grow q-py-none" />

        <!-- Transaction Counter and workspace group icon -->
        <q-list class="q-pt-md">
          <div
            v-if="!isMiniNavigation"
            class="q-py-md"
          >
            <!-- Transaction Counter -->
            <!--
            <div class="transaction-counter-background text-center rounded-borders text-bold q-pa-md q-px-lg">
              <p class="text-caption q-mb-xs">
                <span
                  class="text-bold"
                  :class="[$page.props.shared.transaction_counter.text_color_class]"
                >{{ $page.props.shared.transaction_counter.count }}</span>
                <span class="text-bold">{{ $page.props.shared.transaction_counter.count_max }}</span>
              </p>
              <q-linear-progress
                animation-speed="500"
                rounded
                :value="$page.props.shared.transaction_counter.progress"
                :color="$page.props.shared.transaction_counter.color"
                class="q-mb-xs"
              />
              <span class="text-caption text-muted">{{ $page.props.shared.transaction_counter.description }}</span>
            </div>
            -->
          </div>

          <!-- Workpace Group Logo -->
          <q-item class="q-py-md q-px-md q-mb-md text-center">
            <q-img
              :alt="$page.props.shared.wg.name"
              :src="$page.props.shared.wg.logo_light_url"
              style="height: 22px; margin: auto"
              fit="contain"
            />
          </q-item>
        </q-list>
      </div>
    </q-drawer>

    <q-page-container>
      <slot />
    </q-page-container>
  </q-layout>

  <!-- Onboarding -->
  <!-- Onboarding First Intro -->
  <q-dialog
    v-model="onboarding.first_intro_dialog_show"
    persistent
  >
    <q-card
      class="q-pa-lg"
      style="max-width: 420px"
    >
      <q-card-section class="text-center">
        <q-btn
          icon="close"
          flat
          round
          dense
          class="absolute-top-right"
          @click="onboarding_first_intro_dialog_close"
        />
        <img
          src="/illustrations/tax_report_ob4.svg"
          class="inline-block h-[200px]"
        >
        <h4 class="text-h4 q-mt-none q-mb-md">
          {{ onboarding.first_intro_dialog_title }}
        </h4>
        <p>
          {{ onboarding.first_intro_dialog_description }}
        </p>
      </q-card-section>

      <q-card-actions
        class="q-pa-md"
        align="center"
      >
        <q-btn
          :label="onboarding.first_intro_dialog_action"
          color="primary"
          class="full-width"
          unelevated
          @click="onboarding_first_intro_dialog_action"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
  <!-- Onboarding First Account Created -->
  <q-dialog
    v-model="onboarding.first_account_dialog_show"
    persistent
  >
    <q-card
      class="q-pa-lg"
      style="max-width: 530px"
    >
      <q-carousel
        v-model="onboarding_first_account_created_dialog_slide"
        animated
        class="announcement-carousel q-pa-none"
      >
        <!-- Slide 1 -->
        <q-carousel-slide
          :name="1"
          class="q-pa-none"
        >
          <q-card-section class="text-center">
            <q-btn
              icon="close"
              flat
              round
              dense
              class="absolute-top-right"
              @click="onboarding_first_account_created_dialog_close"
            />
            <img
              src="/illustrations/hourglass.png"
              class="inline-block q-my-md h-[160px]"
            >
            <h4 class="text-h4 q-mt-none q-mb-md">
              {{ onboarding.first_account_dialog_slide_1_title }}
            </h4>
            <p
              class="q-mb-md"
              v-html="onboarding.first_account_dialog_slide_1_description"
            />
          </q-card-section>
        </q-carousel-slide>

        <!-- Slide 2 -->
        <q-carousel-slide
          :name="2"
          class="q-pa-none"
        >
          <q-card-section class="text-center">
            <q-btn
              icon="close"
              flat
              round
              dense
              class="absolute-top-right"
              @click="onboarding_first_account_created_dialog_close"
            />
            <img
              src="/illustrations/onboarding_categorize.png"
              class="inline-block q-my-md h-[160px]"
            >
            <h4 class="text-h4 q-mt-none q-mb-md">
              {{ onboarding.first_account_dialog_slide_2_title }}
            </h4>
            <p
              class="q-mb-md"
              v-html="onboarding.first_account_dialog_slide_2_description"
            />
          </q-card-section>
        </q-carousel-slide>

        <!-- Slide 3 -->
        <q-carousel-slide
          :name="3"
          class="q-pa-none"
        >
          <q-card-section class="text-center">
            <q-btn
              icon="close"
              flat
              round
              dense
              class="absolute-top-right"
              @click="onboarding_first_account_created_dialog_close"
            />
            <img
              src="/illustrations/network.png"
              class="inline-block q-my-md h-[160px]"
            >
            <h4 class="text-h4 q-mt-none q-mb-md">
              {{ onboarding.first_account_dialog_slide_3_title }}
            </h4>
            <p
              class="q-mb-md"
              v-html="onboarding.first_account_dialog_slide_3_description"
            />
          </q-card-section>
        </q-carousel-slide>
      </q-carousel>

      <q-card-section class="q-pt-none">
        <div class="custom-dots row items-center justify-center q-mb-md">
          <div
            class="dot q-mx-xs cursor-pointer"
            :class="{
              active: onboarding_first_account_created_dialog_slide == 1,
            }"
            @click="onboarding_first_account_created_dialog_slide = 1"
          />
          <div
            class="dot q-mx-xs cursor-pointer"
            :class="{
              active: onboarding_first_account_created_dialog_slide == 2,
            }"
            @click="onboarding_first_account_created_dialog_slide = 2"
          />
          <div
            class="dot q-mx-xs cursor-pointer"
            :class="{
              active: onboarding_first_account_created_dialog_slide == 3,
            }"
            @click="onboarding_first_account_created_dialog_slide = 3"
          />
        </div>

        <!-- Slide 1 actions -->
        <div
          v-if="onboarding_first_account_created_dialog_slide == 1"
          class="q-mt-md justify-center row"
        >
          <q-btn
            :label="onboarding.first_account_dialog_next"
            color="primary"
            icon-right="chevron_right"
            unelevated
            @click="onboarding_first_account_created_dialog_slide++"
          />
        </div>

        <!-- Slide 2 actions -->
        <div
          v-else-if="onboarding_first_account_created_dialog_slide == 2"
          class="q-mt-md justify-center row"
        >
          <q-btn
            :label="onboarding.first_account_dialog_next"
            color="primary"
            icon-right="chevron_right"
            unelevated
            @click="onboarding_first_account_created_dialog_slide++"
          />
        </div>

        <!-- Slide 3 actions -->
        <div
          v-else-if="onboarding_first_account_created_dialog_slide == 3"
          class="q-mt-md justify-center row"
        >
          <q-btn
            :label="$q.lang.label.ok"
            color="primary"
            outline
            class="q-mb-sm"
            unelevated
            @click="onboarding_first_account_created_dialog_close"
          />
          <q-btn
            :label="onboarding.first_account_dialog_action"
            color="primary"
            class="q-mb-sm q-ml-md"
            unelevated
            @click="onboarding_first_account_created_dialog_action"
          />
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>
// Quasar language packs
import frQuasar from "quasar/lang/fr";
import enQuasar from "quasar/lang/en-US";

import AjaxBar from "@/shared/ajax_bar.vue";
import Jdenticon from "@/shared/jdenticon.vue";
import UserMenu from "@/shared/user_menu.vue";
import { Link } from "@inertiajs/vue3";
import ClipboardCopy from "@/shared/clipboard_copy.vue";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip } from "chart.js";
import { Line } from "vue-chartjs";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

export default {
  components: {
    AjaxBar,
    ClipboardCopy,
    Jdenticon,
    Link,
    Line,
    UserMenu,
  },
  props: {
    shared: {
      type: Object,
      required: true,
    },
    transactions_counts: {
      type: Object,
      required: true,
    },
    omnisearch_results: {
      type: Object,
      required: true,
    },
    currency_converter_results: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currency_converter_amount: 1,
      currency_converter_search_loading: false,
      currency_converter_range_enabled: false,
      currency_converter_selected_date: null,
      currency_converter_date_from: null,
      currency_converter_date_to: null,
      omnisearch_results_query: "",
      omnisearch_loading: false,
      omnisearch_dropdown_show: true,
      omnisearch_history: [],
      onboarding_first_account_created_dialog_slide: 1,
      drawer: false,
      transaction_details_sidebar_open: false,
      workspaces_selector_dropdown_show: false,
      wacState: this.shared?.wac ? { ...this.shared.wac } : null,
      wacPollingInterval: null,
    };
  },
  computed: {
    currency_converter_chart_color() {
      if (typeof window === "undefined") {
        return "#7c5dfa";
      }
      const accent = getComputedStyle(document.documentElement).getPropertyValue("--q-accent");
      return (accent && accent.trim()) || "#7c5dfa";
    },
    isTransactionsPageActive() {
      return this.shared?.menu_items?.transactions?.is_active;
    },
    wacHeader() {
      return this.wacState;
    },
    isAuditChipCompact() {
      return !!this.$q?.screen?.lt?.lg;
    },
    auditIssuesCount() {
      return this.wacHeader?.issues_count || 0;
    },
    wacI18n() {
      return this.shared?.wac_i18n || {};
    },
    isMiniNavigation() {
      return (
        this.isTransactionsPageActive &&
        this.transaction_details_sidebar_open &&
        this.$q?.screen?.gt.sm
      );
    },
    currency_converter_result() {
      const rate = Number(this.currency_converter_results.rate);
      if (!Number.isFinite(rate)) {
        return "---";
      }

      const amount = Number(this.currency_converter_amount);
      const result = amount * rate;
      return new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: "currency",
        currency: this.currency_converter_results.fiat,
      }).format(result);
    },
    currency_converter_rate_columns() {
      return [
        {
          name: "date",
          label: this.shared.currency_converter.date_label,
          align: "left",
          field: (row) => row.date,
        },
        {
          name: "value",
          label: this.shared.currency_converter.result_label,
          align: "right",
          field: (row) => row.value,
          format: (val) => this.formatRateValue(val),
        },
      ];
    },
    currency_converter_rate_rows() {
      return this.currency_converter_results.rates || [];
    },
    currency_converter_chart_data() {
      if (!this.currency_converter_range_enabled) {
        return null;
      }
      const rows = this.currency_converter_rate_rows;
      const points = (rows || [])
        .map((row) => ({
          label: row.date || row.date_iso,
          value: Number(row.value),
        }))
        .filter((p) => Number.isFinite(p.value));

      if (!points.length) {
        return null;
      }

      return {
        labels: points.map((p) => p.label),
        datasets: [
          {
            data: points.map((p) => p.value),
            borderColor: this.currency_converter_chart_color,
            backgroundColor: "transparent",
            borderWidth: 2,
            tension: 0.25,
            pointRadius: 2,
            pointHoverRadius: 4,
            pointHitRadius: 8,
          },
        ],
      };
    },
    currency_converter_chart_options() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            callbacks: {
              label: (ctx) =>
                `${ctx.parsed.y.toLocaleString(this.$q?.lang?.isoName || "fr-FR", {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 8,
                })}`,
            },
          },
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
            },
            ticks: {
              display: false,
            },
          },
          y: {
            display: true,
            grid: {
              display: false,
            },
            ticks: {
              display: false,
            },
          },
        },
        elements: {
          line: {
            capBezierPoints: true,
          },
        },
        layout: {
          padding: {
            top: 4,
            left: 4,
            right: 4,
            bottom: 4,
          },
        },
      };
    },
    omnisearch_history_show() {
      if (this.omnisearch_results_query.length == 0) {
        return this.omnisearch_history;
      } else {
        return [];
      }
    },
    omnisearch_results_empty() {
      return (
        this.omnisearch_results_query.length != 0 &&
        this.omnisearch_results.exchange_accounts.length == 0 &&
        this.omnisearch_results.assets.length == 0 &&
        this.omnisearch_results.transaction_kinds.length == 0 &&
        this.omnisearch_results.pages.length == 0
      );
    },
    onboarding() {
      return this.$page.props.shared.onboarding;
    },
  },
  watch: {
    "shared.intercom.chat_messages_allowed": {
      handler(allowed) {
        if (allowed) {
          this.$intercom.launcher_show();
        } else {
          this.$intercom.launcher_hide();
        }
      },
      immediate: true,
    },
    "$page.props.transaction_details": {
      handler(newVal) {
        this.transaction_details_sidebar_open = !!newVal;
      },
      immediate: true,
    },
    currency_converter_results: {
      handler(newVal) {
        const rangeEnabled = !!newVal.range_enabled;
        this.currency_converter_range_enabled = rangeEnabled;

        if (newVal.date) {
          this.currency_converter_selected_date = newVal.date;
        } else if (!rangeEnabled && !this.currency_converter_selected_date) {
          this.currency_converter_selected_date =
            this.defaultCurrencyConverterDate();
        }

        if (rangeEnabled) {
          this.currency_converter_date_from =
            newVal.date_from ||
            this.currency_converter_date_from ||
            this.defaultCurrencyConverterDate();
          this.currency_converter_date_to =
            newVal.date_to ||
            this.currency_converter_date_to ||
            this.currency_converter_date_from;
        } else {
          this.currency_converter_date_from = null;
          this.currency_converter_date_to = null;
        }
      },
      immediate: true,
      deep: true,
    },
    "omnisearch_results.query": {
      handler: function (new_value) {
        this.omnisearch_results_query = new_value;
      },
    },
    "shared.workspace.id": {
      handler(newId, oldId) {
        if (newId !== oldId) {
          this.stopWacPolling();
        }
      },
    },
    "shared.wac": {
      handler(newWac) {
        this.stopWacPolling();
        this.wacState = newWac ? { ...newWac } : null;

        if (
          this.wacState?.update_status === "calculating" ||
          this.wacState?.polling
        ) {
          this.startWacPolling();
        }
      },
      deep: true,
    },
  },
  beforeUpdate() {
    this.switch_locale(this.$page.props.auth.locale);
  },
  beforeMount() {
    this.switch_locale(this.$page.props.auth.locale);
  },
  mounted() {
    this.$intercom.initialize(this.$page.props.shared.intercom.settings);
    if (this.$page.props.shared.intercom.chat_messages_allowed) {
      this.$intercom.launcher_show();
    }

    this.transaction_details_sidebar_open =
      !!this.$page?.props?.transaction_details;
    this.omnisearch_history_load();
    if (typeof window !== "undefined") {
      window.addEventListener(
        "transaction-details-sidebar-toggle",
        this.handleTransactionDetailsSidebarToggle
      );
    }
    if (this.$q.screen.gt.sm) {
      this.drawer = true;
    }

    if (this.wacHeader?.update_status === "calculating" || this.wacHeader?.polling) {
      this.startWacPolling();
    }
  },
  unmounted() {
    if (typeof window !== "undefined") {
      window.removeEventListener(
        "transaction-details-sidebar-toggle",
        this.handleTransactionDetailsSidebarToggle
      );
    }
    this.stopWacPolling();
    this.$intercom.launcher_close();
  },
  methods: {
    startWacPolling: function () {
      if (this.wacPollingInterval) return;
      if (!this.shared?.workspace?.id) return;

      const onlyProps = ["shared", "audit_results"];

      this.wacPollingInterval = setInterval(() => {
        this.$inertia.reload({
          only: onlyProps,
          preserveState: true,
          preserveScroll: true,
          data: { ajax_bar_skip: "" },
        });
      }, 2000);
    },
    stopWacPolling: function () {
      if (this.wacPollingInterval) {
        clearInterval(this.wacPollingInterval);
        this.wacPollingInterval = null;
      }
    },
    ajax_bar_filter: function (url) {
      return !/ajax_bar_skip/.test(url);
    },
    handleWacUpdate: function () {
      if (!this.wacHeader?.update_action) {
        return;
      }

      if (this.wacState) {
        this.wacState.update_status = "calculating";
        this.wacState.progress = 0;
        this.startWacPolling();
      }

      this.$inertia.post(
        this.wacHeader.update_action,
        {},
        {
          preserveState: true,
          preserveScroll: true,
        }
      );
    },
    handleTransactionDetailsSidebarToggle: function (event) {
      this.transaction_details_sidebar_open = !!event?.detail?.open;
    },
    omnisearch_history_load: function () {
      const wid = this.$page.props.shared.workspace.id;
      const uid = this.$page.props.auth.user.id;
      this.omnisearch_history = this.$q.localStorage.getItem(
        `omnisearch_history_${wid}_${uid}`
      );
      if (!this.omnisearch_history) {
        this.omnisearch_history = [];
      }
    },
    omnisearch_history_save: function () {
      const wid = this.$page.props.shared.workspace.id;
      const uid = this.$page.props.auth.user.id;
      try {
        this.$q.localStorage.set(
          `omnisearch_history_${wid}_${uid}`,
          this.omnisearch_history
        );
      } catch (e) {
        console.log("omnisearch history wasn't successfully saved due to", e);
      }
    },
    omnisearch_history_delete: function (str) {
      const index = this.omnisearch_history.findIndex((item) => {
        return item == str;
      });
      this.omnisearch_history.splice(index, 1);
      this.omnisearch_history_save();
    },
    omnisearch_history_select: function (query) {
      this.omnisearch_results_query = query;
      // Hack to update the input value
      this.$refs.omnisearch.updateInputValue(query);
      this.omnisearch_update(query, () => { });
    },
    omnisearch_results_select: function (result) {
      const query = this.omnisearch_results_query;

      // Move the most recent query to the top.
      const index = this.omnisearch_history.findIndex((item) => {
        return item == query;
      });
      if (index > -1) {
        this.omnisearch_history.splice(index, 1);
      }
      this.omnisearch_history.unshift(query);

      // Limit the history to the last 10 entries.
      if (this.omnisearch_history.length > 10) {
        this.omnisearch_history.length = 10;
      }

      this.omnisearch_history_save();

      // Visit clicked result.
      this.$inertia.get(result.link);
    },
    omnisearch_update: function (query, after_fn) {
      if (!query.length) {
        this.omnisearch_results_query = "";
        after_fn();
        return;
      }

      this.$inertia.get(
        "",
        { omnisearch: query },
        {
          preserveState: true,
          preserveScroll: true,
          only: ["omnisearch_results"],
          onStart: () => {
            this.omnisearch_loading = true;
          },
          onSuccess: () => { },
          onError: (errors) => {
            console.log(errors);
          },
          onFinish: () => {
            this.omnisearch_loading = false;
            after_fn();
          },
        }
      );
    },
    currency_converter_search_update: function (search, after_fn) {
      this.currency_converter_search_loading = true;
      this.currency_converter_filter_update(
        search,
        this.currency_converter_results.asset?.id
      );
      this.currency_converter_search_loading = false;
      after_fn();
    },
    currency_converter_asset_update: function (asset) {
      this.currency_converter_filter_update(
        this.currency_converter_results.search,
        asset?.id
      );
    },
    currency_converter_date_update: function (date) {
      this.currency_converter_selected_date = date;
      this.currency_converter_filter_update(
        this.currency_converter_results.search,
        this.currency_converter_results.asset?.id,
        { date: date }
      );
    },
    currency_converter_date_from_update: function (date) {
      this.currency_converter_date_from = date;
      if (
        this.currency_converter_date_to &&
        this.isDateGreater(date, this.currency_converter_date_to)
      ) {
        this.currency_converter_date_to = date;
      }
      const maxAllowed = this.addDays(date, 365);
      if (
        this.currency_converter_date_to &&
        this.isDateGreater(this.currency_converter_date_to, maxAllowed)
      ) {
        this.currency_converter_date_to = maxAllowed;
      }
      this.currency_converter_apply_range();
    },
    currency_converter_date_to_update: function (date) {
      let adjustedDate = date;
      if (
        this.currency_converter_date_from &&
        this.isDateGreater(this.currency_converter_date_from, adjustedDate)
      ) {
        adjustedDate = this.currency_converter_date_from;
      }
      const maxAllowed = this.currency_converter_date_from
        ? this.addDays(this.currency_converter_date_from, 365)
        : this.addDays(adjustedDate, 365);
      if (
        this.currency_converter_date_from &&
        this.isDateGreater(adjustedDate, maxAllowed)
      ) {
        adjustedDate = maxAllowed;
      }
      this.currency_converter_date_to = adjustedDate;
      this.currency_converter_apply_range();
    },
    currency_converter_toggle_range: function (enabled) {
      if (enabled) {
        if (!this.currency_converter_date_from) {
          this.currency_converter_date_from =
            this.currency_converter_selected_date ||
            this.defaultCurrencyConverterDate();
        }
        if (!this.currency_converter_date_to) {
          this.currency_converter_date_to = this.currency_converter_date_from;
        }
        this.currency_converter_apply_range();
      } else {
        if (!this.currency_converter_selected_date) {
          this.currency_converter_selected_date =
            this.currency_converter_date_from ||
            this.defaultCurrencyConverterDate();
        }
        this.currency_converter_filter_update(
          this.currency_converter_results.search,
          this.currency_converter_results.asset?.id,
          { date: this.currency_converter_selected_date }
        );
      }
    },
    currency_converter_apply_range: function () {
      if (!this.currency_converter_range_enabled) {
        return;
      }
      if (
        !this.currency_converter_date_from ||
        !this.currency_converter_date_to
      ) {
        return;
      }
      const maxAllowed = this.addDays(this.currency_converter_date_from, 365);
      if (
        this.currency_converter_date_to &&
        this.isDateGreater(this.currency_converter_date_to, maxAllowed)
      ) {
        this.currency_converter_date_to = maxAllowed;
      }
      this.currency_converter_filter_update(
        this.currency_converter_results.search,
        this.currency_converter_results.asset?.id,
        { range: true }
      );
    },
    currency_converter_filter_update: function (
      search,
      asset_id,
      options = {}
    ) {
      const params = {
        currency_converter_search: search || "",
        currency_converter_asset: asset_id || "",
      };

      if (
        this.currency_converter_range_enabled &&
        this.currency_converter_date_from &&
        this.currency_converter_date_to
      ) {
        params.currency_converter_date_from = this.currency_converter_date_from;
        params.currency_converter_date_to = this.currency_converter_date_to;
        params.currency_converter_date = "";
      } else {
        params.currency_converter_date_from = "";
        params.currency_converter_date_to = "";
        const selectedDate =
          options.date ||
          this.currency_converter_selected_date ||
          this.currency_converter_results.date;
        params.currency_converter_date = selectedDate || "";
      }

      this.$inertia.get("", params, {
        preserveState: true,
        preserveScroll: true,
        only: ["currency_converter_results"],
        onStart: () => { },
        onSuccess: () => { },
        onError: (errors) => {
          console.log(errors);
        },
        onFinish: () => { },
      });
    },
    currency_converter_export_rates: function () {
      if (!this.currency_converter_results.export_url) {
        return;
      }
      window.open(this.currency_converter_results.export_url, "_blank");
    },
    formatDateForDisplay: function (date) {
      if (!date) {
        return "";
      }
      const parsed = this.parseDateString(date);
      if (!parsed) {
        return "";
      }
      return parsed.toLocaleDateString(this.$q?.lang?.isoName || "fr-FR");
    },
    formatRateValue: function (value) {
      const numberValue = Number(value);
      if (!Number.isFinite(numberValue)) {
        return value;
      }
      return numberValue.toLocaleString(this.$q?.lang?.isoName || "fr-FR", {
        minimumFractionDigits: 4,
        maximumFractionDigits: 8,
      });
    },
    defaultCurrencyConverterDate: function () {
      const fallback = new Date();
      fallback.setDate(fallback.getDate() - 1);
      return this.formatIsoDate(fallback);
    },
    parseDateString: function (dateStr) {
      if (!dateStr) {
        return null;
      }
      const parts = dateStr.split("-");
      if (parts.length !== 3) {
        return null;
      }
      const [year, month, day] = parts.map((part) => Number(part));
      if ([year, month, day].some((num) => Number.isNaN(num))) {
        return null;
      }
      const date = new Date(`${dateStr}T00:00:00`);
      if (Number.isNaN(date.getTime())) {
        return null;
      }
      return date;
    },
    addDays: function (dateStr, days) {
      const date = this.parseDateString(dateStr);
      if (!date) {
        return null;
      }
      date.setDate(date.getDate() + days);
      return this.formatIsoDate(date);
    },
    formatIsoDate: function (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    compareDateStrings: function (a, b) {
      const dateA = this.parseDateString(a);
      const dateB = this.parseDateString(b);
      if (!dateA || !dateB) {
        return 0;
      }
      const timeDiff = dateA.getTime() - dateB.getTime();
      if (timeDiff < 0) {
        return -1;
      }
      if (timeDiff > 0) {
        return 1;
      }
      return 0;
    },
    isDateGreater: function (a, b) {
      if (!a || !b) {
        return false;
      }
      return this.compareDateStrings(a, b) > 0;
    },
    onboarding_first_account_created_dialog_mark_as_seen: function () {
      return this.$rails.post(this.onboarding.update_path, {
        key: this.onboarding.first_account_dialog_announcement_key,
      });
    },
    onboarding_first_account_created_dialog_action: async function () {
      await this.onboarding_first_account_created_dialog_mark_as_seen();
      this.$inertia.get(this.onboarding.first_account_dialog_action_link);
    },
    onboarding_first_account_created_dialog_close: async function () {
      await this.onboarding_first_account_created_dialog_mark_as_seen();
      this.$inertia.get("", {}, { only: ["shared"] });
    },
    onboarding_first_intro_dialog_mark_as_seen: async function () {
      return this.$rails.post(this.onboarding.update_path, {
        key: this.onboarding.first_intro_dialog_announcement_key,
      });
    },
    onboarding_first_intro_dialog_action: async function () {
      await this.onboarding_first_intro_dialog_mark_as_seen();
      this.$inertia.get(this.onboarding.first_intro_dialog_action_link);
    },
    onboarding_first_intro_dialog_close: async function () {
      await this.onboarding_first_intro_dialog_mark_as_seen();
      this.$inertia.get("", {}, { only: ["shared"] });
    },
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    switch_locale(locale) {
      let new_locale;
      switch (locale) {
      case "fr":
        new_locale = frQuasar;
        break;
      case "en":
        new_locale = enQuasar;
        break;
      default:
        new_locale = frQuasar;
      }
      if (new_locale.isoName != this.$q.lang.isoName) {
        this.$q.lang.set(new_locale);
      }
    },
    workspaces_selector_click: function (workspace) {
      this.workspaces_selector_dropdown_show = false;
      this.$inertia.get(workspace.link);
    },
    workspaces_selector_filter_clients_update: function (checked) {
      this.$inertia.get(
        "",
        {
          workspaces_selector_search_query:
            this.shared.workspaces_selector.search_query,
          workspaces_selector_filter_clients: checked,
        },
        {
          only: ["shared"],
          replace: true,
        }
      );
    },
    workspaces_selector_search_update: function (query) {
      this.$inertia.get(
        "",
        {
          workspaces_selector_search_query: query,
          workspaces_selector_filter_clients:
            this.shared.workspaces_selector.filter_clients_value,
        },
        {
          only: ["shared"],
          replace: true,
        }
      );
    },
  },
};
</script>
<style scoped>
.announcement-carousel {
  height: 100%;
  background: transparent;
}

.dot {
  width: 20px;
  height: 6px;
  border-radius: 4px;
  background: #e0e0e0;
  transition: all 0.3s;
}

.dot.active {
  background: var(--q-primary);
  transform: scale(1.2);
}

.workspace_layout_banner {
  background: linear-gradient(135deg, #ff7a18 0%, #ff3d4a 100%);
}
</style>
<style>
.workspaces_selector_dropdown_menu {
  min-width: 200px;
  max-height: 80vh !important;
  /* Force hidden overflow on Y-axis to prevent the outer scrollbar */
  overflow-y: hidden !important;
  overflow-x: hidden;
}

.workspaces_selector_header,
.workspaces_selector_footer {
  background: var(--menu-background);
  flex-shrink: 0;
}

.workspaces_selector_panel {
  /* Keep an explicit limit so the inner list knows when to scroll */
  max-height: 80vh;
  overflow: hidden;
}

.workspaces_selector_list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.body--dark .workspace-selector-btn {
  background-color: #2c2c2c !important;
  border-color: #2c2c2c !important;
  color: var(--text-default) !important;
}

.body--dark .workspace-selector-btn .q-btn__content,
.body--dark .workspace-selector-btn .q-icon {
  color: var(--text-default) !important;
}

.workspace-selector-btn .q-btn__content {
  justify-content: flex-start;
}

.menu-chip {
  padding: 4px 8px !important;
  background-color: #f0f0f0 !important;
  color: #1f1f1f !important;
}

.body--dark .menu-chip {
  background-color: #2c2c2c !important;
  color: var(--text-default) !important;
}

.workspace-chip {
  background-color: #f0f0f0 !important;
  color: #1f1f1f !important;
  border: none !important;
}

.body--dark .workspace-chip {
  background-color: #2c2c2c !important;
  color: var(--text-default) !important;
  border: none !important;
}

.workspaces_selector_header .q-field {
  border-bottom: 1px solid #0d0d0d1a;
  border-radius: 0;
}

.body--dark .workspaces_selector_header .q-field {
  border-bottom: 1px solid #ffffff0d;
}

.workspaces_selector_header .q-field__native {
  outline: none;
  background-color: transparent;
}

.workspaces_selector_header .q-field__control {
  background-color: transparent !important;
  box-shadow: none !important;
  border-radius: 8px;
}

.workspaces_selector_header .q-field__control:before,
.workspaces_selector_header .q-field__control:after {
  border-color: transparent !important;
  box-shadow: none !important;
}

.workspace-drawer--mini {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.workspace-drawer--mini .q-list {
  padding-left: 0;
  padding-right: 0;
}

.workspace-selector-mini {
  padding-left: 0;
  padding-right: 0;
  margin-left: 0 !important;
  margin-right: 0 !important;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.workspace-selector-btn--mini {
  width: 56px;
  min-width: 56px;
  height: 56px;
  padding: 0 !important;
  border-radius: 50%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.workspace-selector-btn--mini .q-btn__content {
  justify-content: center;
  padding: 0;
}

.workspace-selector-btn--mini .q-btn-dropdown__arrow {
  display: none;
}

.workspace-selector-btn--mini .q-btn-dropdown__arrow-container {
  display: none;
}

.main-menu-link--mini {
  justify-content: center;
  margin-left: 8px;
  margin-right: 8px;
  border-radius: 12px;
  padding-left: 4px;
  padding-right: 4px;
}

.main-menu-link--mini .q-item__section:not(.q-item__section--avatar) {
  display: none;
}

.menu-chip--floating {
  transform: translate(6px, -6px);
}

.currency-converter__menu .transactions-settings-dialog__form {
  box-shadow: none;
  border: none;
}

.currency-converter__chart-wrapper {
  margin-top: 12px;
  margin-bottom: 8px;
}

.currency-converter__chart {
  width: 100%;
  height: 120px;
}

.currency-converter__table {
  max-height: 260px;
  overflow-y: auto;
}
</style>
